import { CommonModule } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { forkJoin } from 'rxjs';
import { IndexService } from '../../../services';
import { Spinner } from '../../../shared/spinner/spinner';
import { ToastService } from '../../../services/toast-service';
import { HttpErrorResponse } from '@angular/common/http';
import { ConfigItem, CreateIndexDto } from '../../../shared/interfaces';

@Component({
    selector: 'app-new-index-modal',
    standalone: true,
    imports: [CommonModule, Spinner],
    templateUrl: './new-index-modal.html'
})
export class NewIndexModalComponent {
    activeModal = inject(NgbActiveModal);
    private readonly indexService = inject(IndexService);
    public toastService = inject(ToastService);

    // Dati per le Select
    availableEmbedders = signal<ConfigItem[]>([]);
    availableProviders = signal<ConfigItem[]>([]);
    isLoadingConfig = signal(true);
    hasError = signal(false);

    // Valori del Form
    indexName = signal('');
    indexId = signal('');
    private readonly isIdManuallyEdited = signal(false);
    selectedEmbedder = signal('');
    selectedProvider = signal('');

    // Stato creazione
    isCreating = signal(false);

    constructor() {
        this.loadConfig();
    }

    loadConfig() {
        this.isLoadingConfig.set(true);
        // Usiamo forkJoin per chiamare le due API in parallelo
        forkJoin({
            embedders: this.indexService.getAvailableEmbedders(),
            providers: this.indexService.getAvailableProviders()
        }).subscribe({
            next: (results) => {
                this.availableEmbedders.set(results.embedders);
                this.availableProviders.set(results.providers);
                this.isLoadingConfig.set(false);
            },
            error: (err: unknown) => {
                console.error('Errore caricamento configurazioni:', err);
                this.isLoadingConfig.set(false);
                this.hasError.set(true);
            }
        });
    }

    onNameChange(event: Event) {
        const val = (event.target as HTMLInputElement).value;
        this.indexName.set(val);

        // Se l'utente non ha modificato a mano l'ID, suggeriamo uno slug valido
        if (!this.isIdManuallyEdited()) {
            let slug = val
                .toLowerCase()
                .trim()
                .replaceAll(/[^a-z0-9_-]+/g, '_');
            while (slug.startsWith('_')) {
                slug = slug.slice(1);
            }
            while (slug.endsWith('_')) {
                slug = slug.slice(0, -1);
            }
            this.indexId.set(slug);
        }
    }

    onIdChange(event: Event) {
        const val = (event.target as HTMLInputElement).value;
        this.isIdManuallyEdited.set(true);
        this.indexId.set(val);
    }

    onEmbedderChange(event: Event) {
        const val = (event.target as HTMLSelectElement).value;
        this.selectedEmbedder.set(val);
    }

    onProviderChange(event: Event) {
        const val = (event.target as HTMLSelectElement).value;
        this.selectedProvider.set(val);
    }

    confirmCreation() {
        // Validazione base
        if (!this.indexId() || !this.selectedEmbedder() || !this.selectedProvider()) {
            return;
        }

        this.isCreating.set(true);

        const payload: CreateIndexDto = {
            name: this.indexId().trim(),
            description: this.indexName().trim() || this.indexId().trim(),
            embedder: this.selectedEmbedder(),
            provider: this.selectedProvider()
        };

        this.indexService.createIndex(payload).subscribe({
            next: () => {
                this.isCreating.set(false);
                this.activeModal.close(true); // Restituisce true per indicare successo
            },
            error: (response: HttpErrorResponse) => {
                console.error('Errore creazione indice:', response);
                this.isCreating.set(false); 
                this.toastService.show(`Errore durante la creazione dell'indice: ${response.error.message}`, 'error');
            }
        });
    }

    dismiss() {
        this.activeModal.dismiss();
    }
}
